import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Home, MessageSquare, ShoppingBag, Sparkles, User } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const NAV_ITEMS = [
  { key: "feed", icon: Home, path: "/", testId: "nav-feed" },
  { key: "messages", icon: MessageSquare, path: "/messages", testId: "nav-messages", messagesBadge: true },
  { key: "marketplace", icon: ShoppingBag, path: "/marketplace", testId: "nav-marketplace" },
  { key: "stx", icon: Sparkles, path: "/stilltouchedx", testId: "nav-stilltouchedx" },
  { key: "profile", icon: User, path: "/profile", testId: "nav-profil", watchBadge: true },
];

export default function BottomNav() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [unread, setUnread] = useState(0);
  const [watchUnread, setWatchUnread] = useState(0);

  useEffect(() => {
    if (!user || user === false) return;
    const load = async () => {
      try { const { data } = await api.get("/messages/unread"); setUnread(data.total || 0); } catch { /* ignore */ }
      try { const { data } = await api.get("/me/watch-notifications/summary"); setWatchUnread(data.total || 0); } catch { /* ignore */ }
    };
    load();
    const interval = setInterval(load, 30000);
    const onRefresh = () => { load(); };
    window.addEventListener("unread-refresh", onRefresh);
    window.addEventListener("watch-unread-refresh", onRefresh);
    return () => {
      clearInterval(interval);
      window.removeEventListener("unread-refresh", onRefresh);
      window.removeEventListener("watch-unread-refresh", onRefresh);
    };
  }, [user]);

  const isActive = (path) => {
    if (path === "/") return pathname === "/";
    return pathname.startsWith(path);
  };

  return (
    <nav data-testid="bottom-navigation"
      className="fixed bottom-0 left-0 right-0 bg-white border-t border-[#E5E5E5] z-50 h-16 flex items-center justify-around px-1 sm:px-4 md:px-8">
      <div className="w-full max-w-5xl mx-auto flex items-center justify-around">
      {NAV_ITEMS.map((item) => {
        const active = isActive(item.path);
        const count = item.messagesBadge ? unread : item.watchBadge ? watchUnread : 0;
        const badgeTestId = item.messagesBadge ? "unread-badge" : item.watchBadge ? "profile-watch-badge" : undefined;
        return (
          <button key={item.path} data-testid={item.testId} onClick={() => navigate(item.path)}
            className={`relative flex flex-col items-center justify-center gap-0.5 flex-1 py-1 transition-colors active:scale-[0.95] ${active ? "text-[#0A0A0A]" : "text-[#999]"}`}>
            <item.icon size={20} strokeWidth={active ? 2.5 : 1.5} />
            {(item.messagesBadge || item.watchBadge) && count > 0 && (
              <span data-testid={badgeTestId} className="absolute -top-0.5 right-1/4 w-4 h-4 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">
                {count > 9 ? "9+" : count}
              </span>
            )}
            <span className="text-[10px] font-medium tracking-wide">{t(`nav.${item.key}`)}</span>
          </button>
        );
      })}
      </div>
    </nav>
  );
}
